import { computed } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import { ChecklistCheckRowFragment } from './fragments'
import { FragmentType, graphql, useFragment } from '@/gql'
import { ChecklistCheckState, ChecklistStatus } from '@/gql/graphql'

/** The save every checklist row shares: change a check's state, refreshing the offer gate only when it matters. */
export function useChecklistCheck(source: () => FragmentType<typeof ChecklistCheckRowFragment>) {
  const check = computed(() => useFragment(ChecklistCheckRowFragment, source()))

  const { mutate, loading } = useMutation(
    graphql(`
      mutation SetChecklistCheckState($input: SetChecklistCheckStateInput!) {
        setChecklistCheckState(input: $input) {
          ...ChecklistCheckRow
        }
      }
    `),
    { clientId: 'publicApi' },
  )

  async function setState(state: ChecklistCheckState, reason?: string): Promise<boolean> {
    // No optimistic move: the row stays put (showing a spinner) until the server answers, so a check never
    // jumps between the open and finished lists mid-save and its Undo can't fire against a pending request.
    const wasBlocked = check.value.checklist.status === ChecklistStatus.Blocked

    try {
      await mutate(
        { input: { check: check.value.id, state, reason } },
        {
          // The offer gate only changes when the checklist starts or stops blocking.
          refetchQueries: ({ data }) =>
            data && (data.setChecklistCheckState.checklist.status === ChecklistStatus.Blocked) !== wasBlocked
              ? ['HireGateQuery']
              : [],
        },
      )
    } catch {
      // The global Apollo error link shows the notice; the row keeps its current state and typed reason.
      return false
    }

    return true
  }

  return { check, loading, setState }
}
